import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  BellRing,
  Users,
  BarChart3,
  LogOut,
  FileText,
  MessageSquare,
  LifeBuoy,
  Settings,
  Layers,
  DollarSign,
  Palette,
  AlertTriangle,
  Megaphone,
  Mail,
  Search,
  TrendingUp,
  Handshake,
  ChevronDown,
  UserCog,
  ChevronRight,
  PenTool,
  Brain,
  Library,
  Package,
  Activity,
  Monitor,
  GitBranch,
  Smartphone,
  Map,
  Microscope,
  FlaskConical,
  KeyRound,        // Codes: Store inventory (and its Store setup tab — was Partners → Store Access)
  Ticket,          // Codes: the group, and Issue codes
  Percent,         // Codes: Offers & discounts
  Plug,            // Codes: API
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
  // Phase 6 polish v23 (2026-05-13): nav refresh
  ListTodo,        // new Roadmap icon — it's really a backlog
  Star,            // favorite toggle on every nav item (gold star)
  GripVertical,    // drag handle in Favorites section
  Menu,            // mobile: open nav drawer (hamburger)
  X,               // mobile: close nav drawer
} from 'lucide-react';
// dnd-kit family is already a dependency (used by the Roadmap Kanban
// view). Reusing it here means no new npm install.
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/apiConfig';
import { adminFetch, adminMutate } from '../utils/adminApi';
import { legacyNavPath } from '../pages/admin/codes/codesAreaLogic';

interface AdminLayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
  pageSubtitle?: string;
}

interface QuickStatsData {
  active_users: number;
  total_sessions: number;
  system_health: number;
}

interface NavItem {
  path: string;
  icon: React.FC<any>;
  label: string;
  badge?: number;
}

interface NavGroup {
  label: string;
  icon: React.FC<any>;
  paths: string[]; // all paths that belong to this group (for auto-expand)
  items: NavItem[];
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children, pageTitle, pageSubtitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [quickStats, setQuickStats] = useState<QuickStatsData | null>(null);
  const [supportBadge, setSupportBadge] = useState<number>(0);
  // Alert Center "needs attention" count (sidebar badge + header bell).
  // null = unknown / no alerts.read capability: show no badge.
  const [alertsBadge, setAlertsBadge] = useState<number | null>(null);
  // Account menu (My preferences · Log out) — who is signed in, from /auth/me.
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountLabel, setAccountLabel] = useState<string>('');
  const [modelIntelBadge, setModelIntelBadge] = useState<number>(0);

  // Mobile nav drawer (PR1, 2026-06-21 responsive shell). Below the `md`
  // breakpoint the sidebar becomes an off-canvas drawer toggled from a
  // header hamburger; at `md`+ it stays the in-flow sidebar with the
  // existing collapse rail. Closed on every mount.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Close the drawer on route change so navigating never leaves it open.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Collapsible sidebar (Phase 4 polish v2 — 2026-05-11). When collapsed,
  // the nav shrinks to an icon-only 56px rail so the main content area
  // gains ~210px of width. State persisted via the storage service
  // (CLAUDE.md mandates this over direct localStorage). Initial render
  // uses the synchronous fallback (false) — useEffect below rehydrates
  // from storage on mount.
  //
  // NOTE: `sidebarCollapsed` is the DESKTOP collapse preference (persisted).
  // Below the `md` breakpoint the nav is an off-canvas drawer, where an
  // icon-only rail is meaningless (it shows empty white space with no
  // labels). The render reads `effectiveCollapsed` (derived below), which
  // forces the full labeled nav on mobile regardless of this preference.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await storage.getItem('adminSidebar.collapsed');
        if (!cancelled && v === 'true') setSidebarCollapsed(true);
      } catch {
        /* no-op — sidebar just stays expanded if storage fails */
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      // Best-effort persist; storage errors don't break the toggle.
      storage.setItem('adminSidebar.collapsed', next ? 'true' : 'false').catch(() => {});
      return next;
    });
  };

  // Track whether we're below the `md` (768px) breakpoint, where the nav
  // becomes an off-canvas drawer. matchMedia keeps this in sync as the
  // browser is resized (the half-screen case Matt reported). SSR-safe
  // initial read; the effect re-subscribes on mount.
  const [isBelowMd, setIsBelowMd] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setIsBelowMd(e.matches);
    // Sync once in case the breakpoint changed between initial render and mount.
    setIsBelowMd(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Effective collapse state used by the render. On mobile (drawer) the nav
  // must ALWAYS show the full labeled list — an icon-only rail inside a
  // full-width drawer is the bug Matt reported. So collapse only takes
  // effect at md+ widths. (The raw `sidebarCollapsed` state above is the
  // persisted desktop preference + the toggle's setter target.)
  const effectiveCollapsed = sidebarCollapsed && !isBelowMd;

  // Phase 6 polish v23 (2026-05-13): favorites/pins. Each entry is a
  // nav-item path (e.g. '/admin/users'). The array order is the
  // render order — drag-to-reorder rewrites the array. Lookup of
  // label + icon happens at render time from `navItemsByPath` (built
  // below from the canonical navigationGroups), so favorites stay in
  // sync with any nav-config edits (renames, icon swaps, etc.).
  //
  // Standalone top-level items (Dashboard, Roadmap) are intentionally
  // NOT favoritable — they're already prominent and pinning them
  // would duplicate them in the sidebar.
  const [favorites, setFavorites] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await storage.getItem('adminNav.favorites');
        if (!cancelled && raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            // A favorite saved under a moved page's old path (/admin/growth, /admin/store-access, …)
            // follows the page to its new one (Codes PR-8).
            setFavorites(Array.from(new Set(parsed.filter((p): p is string => typeof p === 'string').map(legacyNavPath))));
          }
        }
      } catch {
        /* storage / JSON failures just leave the list empty */
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const persistFavorites = (next: string[]) => {
    setFavorites(next);
    storage.setItem('adminNav.favorites', JSON.stringify(next)).catch(() => {});
  };
  const toggleFavorite = (path: string) => {
    persistFavorites(
      favorites.includes(path)
        ? favorites.filter((p) => p !== path)
        : [...favorites, path],
    );
  };
  const isFavorited = (path: string) => favorites.includes(path);

  // CC LLMs nav state — kept for legacy refs but unused after the nav was
  // promoted to a top-level group (peer to Platform Analytics). The standard
  // group expand/collapse machinery handles it now.
  const [marketingExpanded, setMarketingExpanded] = useState(
    location.pathname.startsWith('/admin/marketing')
  );

  // Fetch support ticket badge count
  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const token = await storage.getItem('admin_token');
        const res = await fetch(`${API_BASE_URL}/api/admin/support/stats`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setSupportBadge(data.new_count || 0);
        }
      } catch {
        // Silently fail — badge just won't show
      }
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    adminFetch<{ admin: { full_name: string | null; email: string } }>('/api/admin/auth/me')
      .then(d => setAccountLabel(d.admin.full_name || d.admin.email || ''))
      .catch(() => setAccountLabel(''));
  }, []);

  // Fetch Alert Center "needs attention" count
  useEffect(() => {
    const fetchAlertsBadge = async () => {
      try {
        const data = await adminFetch<{ needs_attention: number }>('/api/admin/alerts/summary');
        setAlertsBadge(data.needs_attention ?? 0);
      } catch {
        setAlertsBadge(null); // 403 for admins without alerts.read, or offline
      }
    };
    fetchAlertsBadge();
    const interval = setInterval(fetchAlertsBadge, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Model Intelligence alert badge count
  useEffect(() => {
    const fetchModelIntelBadge = async () => {
      try {
        const token = await storage.getItem('admin_token');
        const res = await fetch(`${API_BASE_URL}/api/admin/llm-optimization/alerts?status=active&limit=1`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setModelIntelBadge(data.count || 0);
        }
      } catch {
        // Silently fail
      }
    };
    fetchModelIntelBadge();
    const interval = setInterval(fetchModelIntelBadge, 120000);
    return () => clearInterval(interval);
  }, []);

  // Fetch quick stats data - DISABLED (not critical for admin portal)
  // The /api/analytics/dashboard endpoint expects user JWT tokens, not admin session tokens
  // Quick stats are visible in the Comprehensive Dashboard tab instead
  useEffect(() => {
    setQuickStats({
      active_users: 0,
      total_sessions: 0,
      system_health: 100
    });
  }, []);

  const navigationGroups: NavGroup[] = [
    {
      label: 'Platform Health',
      icon: Monitor,
      paths: ['/admin/system-alerts', '/admin/debriefs-audit', '/admin/db-inspector', '/admin/app-version'],
      items: [
        { path: '/admin/system-alerts', icon: AlertTriangle, label: 'System Alerts' },
        { path: '/admin/debriefs-audit', icon: FileText, label: 'Debrief Audit' },
        { path: '/admin/db-inspector', icon: Search, label: 'DB Inspector' },
        { path: '/admin/app-version', icon: Smartphone, label: 'App Version' },
      ]
    },
    {
      label: 'Analytics',
      icon: BarChart3,
      paths: ['/admin/subscription-analytics', '/admin/analytics'],
      items: [
        { path: '/admin/analytics', icon: BarChart3, label: 'Platform Analytics' },
        { path: '/admin/subscription-analytics', icon: DollarSign, label: 'Subscription Analytics' },
      ]
    },
    {
      label: 'CC LLMs',
      icon: Brain,
      paths: ['/admin/llm-profiles', '/admin/llm-usage', '/admin/model-intelligence', '/admin/model-library'],
      items: [
        { path: '/admin/llm-profiles', icon: Settings, label: 'LLM Profiles' },
        { path: '/admin/llm-usage', icon: BarChart3, label: 'LLM Usage' },
        { path: '/admin/model-intelligence', icon: Brain, label: 'Model Intelligence' },
        { path: '/admin/model-library', icon: Library, label: 'Model Library' },
      ]
    },
    {
      label: 'Users',
      icon: Users,
      // App users only. In-app Campaigns joined from Marketing & Comms (Paul, 2026-09-09:
      // the portal is for the app and its users); Codes moved to its own group below
      // (Codes PR-8); the admin Team moved to Settings (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md).
      paths: ['/admin/users', '/admin/tiers', '/admin/campaigns'],
      items: [
        { path: '/admin/users', icon: Users, label: 'User Management' },
        { path: '/admin/tiers', icon: Layers, label: 'Tier Management' },
        { path: '/admin/campaigns', icon: Megaphone, label: 'In-app Campaigns' },
      ]
    },
    // The one Codes area (Codes PR-8, the lead's unified-codes design §2.1): every code Coach Cedar
    // issues — Summer50, one-off, referral, partner, Coach Cedar codes — and the App Store code supply.
    // Issue codes sits second: "+ One-off code" and "Generate pool codes" are the owner's daily tools.
    // The old pages redirect (web/src/pages/admin/codes/codesAreaLogic.ts LEGACY_REDIRECTS). Pinned by
    // tests/gate/test_admin_marketing_sidebar_order.py.
    {
      label: 'Codes',
      icon: Ticket,
      paths: ['/admin/codes'],
      items: [
        { path: '/admin/codes', icon: BarChart3, label: 'Overview' },
        { path: '/admin/codes/issue', icon: Ticket, label: 'Issue codes' },
        { path: '/admin/codes/all', icon: Search, label: 'All codes' },
        { path: '/admin/codes/activity', icon: Activity, label: 'Activity' },
        { path: '/admin/codes/offers', icon: Percent, label: 'Offers & discounts' },
        { path: '/admin/codes/store', icon: KeyRound, label: 'Store inventory' },
        { path: '/admin/codes/api', icon: Plug, label: 'API' },
      ]
    },
    {
      label: 'Research',
      icon: Microscope,
      paths: ['/admin/researcher-knowledge', '/admin/research'],
      items: [
        { path: '/admin/researcher-knowledge', icon: FlaskConical, label: 'Researcher Knowledge' },
        { path: '/admin/research/curation-runs', icon: Activity, label: 'Curation runs' },
      ]
    },
    // Marketing Terminal v1 (#817) — 9 modules + Brand Kit + Connections.
    // See docs/MARKETING_TERMINAL_PAUL_PLAYBOOK.md for the launch-day
    // playbook (API signups, daily / weekly loop, troubleshooting).
    //
    // 2026-05-21 reorder: items previously sat in source-of-creation
    // order, which surfaced lower-traffic items (Audience, Listening,
    // Landing Pages) above daily-driver ones (Engagement Inbox,
    // In-app Campaigns). New grouping = top-of-list daily / weekly,
    // middle = sometimes, bottom = set-and-forget config. No
    // click-telemetry exists for admin nav (no admin_audit_log /
    // admin_action_log entries cover navigation), so the ranking is
    // intent-based per the Phase-D-followup audit. Pinned by
    // tests/gate/test_admin_marketing_sidebar_order.py.
    {
      label: 'Marketing & Comms',
      icon: TrendingUp,
      paths: ['/admin/emails', '/admin/marketing'],
      items: [
        // Daily / weekly drivers
        { path: '/admin/marketing', icon: TrendingUp, label: 'Marketing Terminal' },
        { path: '/admin/marketing/engagement', icon: MessageSquare, label: 'Engagement Inbox' },
        { path: '/admin/marketing/social', icon: Megaphone, label: 'Pipeline' },
        { path: '/admin/marketing/analytics', icon: BarChart3, label: 'Analytics' },
        // Sometimes
        { path: '/admin/marketing/audience', icon: Users, label: 'Audience' },
        { path: '/admin/marketing/listening', icon: Search, label: 'Listening' },
        { path: '/admin/marketing/landing-pages', icon: FileText, label: 'Landing Pages' },
        { path: '/admin/marketing/creative', icon: PenTool, label: 'Creative Studio' },
        { path: '/admin/emails', icon: Mail, label: 'Email Management' },
        { path: '/admin/marketing/budgets', icon: DollarSign, label: 'Budgets' },
        { path: '/admin/marketing/media', icon: Mail, label: 'Media Relations' },
        // Set-and-forget config
        { path: '/admin/marketing/brand', icon: Palette, label: 'Brand Kit' },
        { path: '/admin/marketing/settings', icon: Settings, label: 'Connections & Keys' },
      ]
    },
    // Wave 8 partner program (codes platform item C8, 2026-09-27). Its own top-level
    // group (lead decision): the partner record, tags, links, website code and statements.
    // Its codes pages (Coach Cedar codes, Code batches, Store Access) moved into Codes (Codes PR-8).
    {
      label: 'Partners',
      icon: Handshake,
      paths: ['/admin/partners'],
      items: [
        { path: '/admin/partners', icon: Handshake, label: 'Partners' },
      ]
    },
    {
      label: 'Support',
      icon: LifeBuoy,
      paths: ['/admin/support'],
      items: [
        { path: '/admin/support', icon: LifeBuoy, label: 'Tickets', badge: supportBadge > 0 ? supportBadge : undefined },
      ]
    },
    {
      // Phase 6 polish v23 (2026-05-13): Roadmap promoted out of this
      // group into a top-level standalone link (rendered above the
      // groups, between Dashboard and Favorites). Roadmap is the most-
      // used surface and semantically distinct from Product / CI-CD,
      // so it earns the prominence.
      label: 'Product',
      icon: Package,
      paths: ['/admin/product', '/admin/product-updates', '/admin/cicd'],
      items: [
        { path: '/admin/product', icon: Package, label: 'Product Management' },
        { path: '/admin/product-updates', icon: Megaphone, label: 'Product Updates' },
        { path: '/admin/cicd', icon: GitBranch, label: 'CI/CD Pipeline' },
        { path: '/admin/cicd/drift', icon: GitBranch, label: 'Drift' },
      ]
    },
    {
      label: 'Styling',
      icon: Palette,
      paths: ['/admin/lottie-colors'],
      items: [
        { path: '/admin/lottie-colors', icon: Palette, label: 'Lottie Colors' },
      ]
    },
    // Central portal settings (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md).
    // Kept last: set-and-forget. Sections with their own settings keep them.
    {
      label: 'Settings',
      icon: Settings,
      paths: ['/admin/settings', '/admin/team'],
      items: [
        { path: '/admin/settings/general', icon: Settings, label: 'General' },
        { path: '/admin/settings/notifications', icon: BellRing, label: 'Notifications' },
        { path: '/admin/settings/security', icon: Shield, label: 'Security' },
        { path: '/admin/team', icon: Users, label: 'Team' },
        { path: '/admin/settings/audit-log', icon: FileText, label: 'Audit log' },
      ]
    },
  ];

  // Phase 6 polish v23 (2026-05-13): flat map of path → NavItem so the
  // Favorites section can render labels + icons looked up by path. Built
  // from the canonical navigationGroups so renames / icon swaps in the
  // groups automatically propagate to favorites.
  const navItemsByPath: Record<string, NavItem> = {};
  navigationGroups.forEach((group) => {
    group.items.forEach((item) => {
      navItemsByPath[item.path] = item;
    });
  });
  // Only favorites that still exist in the nav config render. Stale
  // pins (e.g. a page that was removed) are silently filtered out so
  // the section never shows a broken link.
  const validFavorites = favorites.filter((p) => navItemsByPath[p] != null);

  // Determine which groups are active based on current path
  const isGroupActive = (group: NavGroup) => {
    return group.paths.some(p => location.pathname === p || location.pathname.startsWith(p + '/') || location.pathname.startsWith(p));
  };

  // Track which groups are expanded — auto-expand active group
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    navigationGroups.forEach(g => {
      if (isGroupActive(g)) initial.add(g.label);
    });
    // No default expanded group — Dashboard is standalone above
    return initial;
  });

  const toggleGroup = (label: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  // Logout ends the session on the server AND forgets it here. Before
  // 2026-09-29 this posted to a relative `/auth/logout` (not the admin route)
  // and never cleared the stored token, so "logged out" admins stayed signed in
  // for the rest of the 8-hour session.
  const handleLogout = async () => {
    try {
      await adminMutate('/api/admin/auth/logout', 'POST');
    } catch (error) {
      // Already-expired sessions land here too; the local session still ends.
      console.warn('Admin logout request failed; clearing the local session anyway', error);
    } finally {
      await storage.removeItem('admin_token').catch(() => {});
      await storage.removeItem('admin_user').catch(() => {});
      navigate('/admin/login');
    }
  };

  // The longest matching item wins: on /admin/codes/all only "All codes" is lit, not also the
  // Codes Overview at /admin/codes (Codes PR-8) — likewise Marketing Terminal vs its sub-pages.
  const matches = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isActive = (path: string) => {
    if (!matches(path)) return false;
    return !Object.keys(navItemsByPath).some((p) => p !== path && p.length > path.length && matches(p));
  };

  const isMarketingActive = location.pathname.startsWith('/admin/marketing');

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--cedar-limestone)' }}>
      {/* Admin Header — Cedar Dark branded */}
      <header style={{ background: 'linear-gradient(135deg, var(--cedar-dark) 0%, var(--cedar-primary) 100%)' }} className="shadow-md">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {/* Mobile hamburger — opens the nav drawer. Hidden at md+ where
                  the in-flow sidebar is always visible. */}
              <button
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open navigation menu"
                className="md:hidden mr-2 -ml-1 p-2 rounded-md transition-colors"
                style={{ color: 'rgba(255,255,255,0.85)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Menu className="h-5 w-5" />
              </button>
              <img
                src="/Logo-white-side.svg"
                alt="Coach Cedar"
                className="w-auto mr-3"
                style={{ height: '34px', maxWidth: '200px' }}
                onError={(e) => {
                  console.error('Logo load failed, trying fallback');
                  (e.target as HTMLImageElement).src = '/coach-cedar-logo.svg';
                  (e.target as HTMLImageElement).classList.add('brightness-0', 'invert');
                }}
              />
              <div>
                <p className="text-sm" style={{ color: 'var(--cedar-mist)', fontFamily: "'League Spartan', sans-serif" }}>Admin Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
            {alertsBadge !== null && (
              <Link
                to="/admin/alerts"
                aria-label={alertsBadge > 0 ? `${alertsBadge} alerts need attention` : 'Alerts'}
                title={alertsBadge > 0 ? `${alertsBadge} alerts need attention` : 'Alerts'}
                className="relative flex items-center rounded-md p-2 transition-colors"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                <BellRing className="h-5 w-5" />
                {alertsBadge > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-red-500 px-1 text-center text-[10px] font-bold leading-[18px] text-white">
                    {alertsBadge > 99 ? '99+' : alertsBadge}
                  </span>
                )}
              </Link>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={() => setAccountOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md transition-colors"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                <UserCog className="h-4 w-4" />
                <span className="hidden max-w-[160px] truncate sm:inline">{accountLabel || 'Account'}</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {accountOpen && (
                <div role="menu" className="absolute right-0 z-50 mt-1 w-48 overflow-hidden rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5"
                  onMouseLeave={() => setAccountOpen(false)}>
                  <Link to="/admin/me" role="menuitem" onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50">
                    <UserCog className="h-4 w-4" /> My preferences
                  </Link>
                  <button type="button" role="menuitem" onClick={() => { setAccountOpen(false); handleLogout(); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-50">
                    <LogOut className="h-4 w-4" /> Log out
                  </button>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer backdrop — only below md, only when open. Tapping it
          closes the drawer. */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation.
              Mobile (< md): off-canvas fixed drawer, slid in/out by
              `mobileNavOpen`. Desktop (md+): in-flow sidebar with the
              existing collapse rail (w-64 / w-14). The limestone background
              matches the page on desktop (invisible) and backs the drawer
              on mobile. Tapping any <a> (nav Link) closes the drawer. */}
          <nav
            className={`fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto p-4 shadow-xl transition-[transform,width] duration-200 md:static md:inset-auto md:z-auto md:w-auto md:overflow-visible md:p-0 md:shadow-none md:flex-shrink-0 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 ${effectiveCollapsed ? 'md:w-14' : 'md:w-64'}`}
            style={{ backgroundColor: 'var(--cedar-limestone)' }}
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('a')) setMobileNavOpen(false);
            }}
          >
            {/* Mobile-only drawer close button */}
            <div className="flex justify-end mb-2 md:hidden">
              <button
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close navigation menu"
                className="p-2 rounded-md hover:bg-gray-100 transition-colors"
                style={{ color: 'var(--text-secondary)' }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div
              className="bg-white p-3"
              style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--card-drop)' }}
            >
              {/* Collapse toggle. When collapsed, the chevron points right
                  (PanelLeftOpen) signalling "click to expand". Centered in
                  the rail. Hidden in the mobile drawer (isBelowMd) — collapse
                  is a desktop space-saving affordance with no meaning inside a
                  full-width off-canvas drawer. */}
              {!isBelowMd && (
                <button
                  onClick={toggleSidebar}
                  aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  className={`flex items-center rounded-lg transition-colors mb-2 hover:bg-gray-50 ${
                    effectiveCollapsed ? 'justify-center w-full py-2' : 'px-3 py-2 w-full'
                  }`}
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {effectiveCollapsed
                    ? <PanelLeftOpen className="h-4 w-4" />
                    : (
                      <>
                        <PanelLeftClose className="h-4 w-4 mr-2.5" />
                        <span className="text-xs font-semibold uppercase tracking-wider" style={{ fontFamily: "'League Spartan', sans-serif" }}>
                          Collapse
                        </span>
                      </>
                    )
                  }
                </button>
              )}

              {/* Dashboard — always visible, top-level */}
              <Link
                to="/admin/dashboard"
                title={effectiveCollapsed ? 'Dashboard' : undefined}
                className={`flex items-center rounded-lg transition-colors mb-2 ${
                  effectiveCollapsed ? 'justify-center py-2.5' : 'px-3 py-2.5'
                } ${isActive('/admin/dashboard') ? 'font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                style={isActive('/admin/dashboard') ? { backgroundColor: 'rgba(31, 108, 79, 0.06)', color: 'var(--cedar-primary)' } : {}}
              >
                <Home
                  className={`h-4 w-4 ${effectiveCollapsed ? '' : 'mr-2.5'}`}
                  style={{ color: isActive('/admin/dashboard') ? 'var(--cedar-primary)' : 'var(--text-secondary)' }}
                />
                {!effectiveCollapsed && <span className="text-sm font-medium">Dashboard</span>}
              </Link>
              {/* Alerts — always visible, top-level: one inbox for every alert
                  (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md WS-I). */}
              <Link
                to="/admin/alerts"
                title={effectiveCollapsed ? 'Alerts' : undefined}
                className={`flex items-center rounded-lg transition-colors mb-2 ${
                  effectiveCollapsed ? 'justify-center py-2.5' : 'px-3 py-2.5'
                } ${isActive('/admin/alerts') ? 'font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                style={isActive('/admin/alerts') ? { backgroundColor: 'rgba(31, 108, 79, 0.06)', color: 'var(--cedar-primary)' } : {}}
              >
                <BellRing
                  className={`h-4 w-4 ${effectiveCollapsed ? '' : 'mr-2.5'}`}
                  style={{ color: isActive('/admin/alerts') ? 'var(--cedar-primary)' : 'var(--text-secondary)' }}
                />
                {!effectiveCollapsed && <span className="flex-1 text-sm font-medium">Alerts</span>}
                {!effectiveCollapsed && alertsBadge !== null && alertsBadge > 0 && (
                  <span className="rounded-full bg-red-500 px-1.5 text-[10px] font-bold leading-[18px] text-white">
                    {alertsBadge > 99 ? '99+' : alertsBadge}
                  </span>
                )}
              </Link>
              {/* Divider — kept from before; sits between Dashboard and Roadmap */}
              <div style={{ height: '1px', backgroundColor: 'var(--cedar-limestone)', margin: '0 8px 8px 8px' }} />

              {/* Roadmap — always visible, top-level. Phase 6 polish v23
                  (2026-05-13): promoted out of the Product group because
                  it's the most-used admin surface and semantically distinct
                  from Product Management / CI-CD. Icon swapped from Map
                  to ListTodo since "backlog" describes the surface better
                  than "roadmap" does today. */}
              <Link
                to="/admin/roadmap"
                title={effectiveCollapsed ? 'Roadmap' : undefined}
                className={`flex items-center rounded-lg transition-colors mb-2 ${
                  effectiveCollapsed ? 'justify-center py-2.5' : 'px-3 py-2.5'
                } ${isActive('/admin/roadmap') ? 'font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                style={isActive('/admin/roadmap') ? { backgroundColor: 'rgba(31, 108, 79, 0.06)', color: 'var(--cedar-primary)' } : {}}
              >
                <ListTodo
                  className={`h-4 w-4 ${effectiveCollapsed ? '' : 'mr-2.5'}`}
                  style={{ color: isActive('/admin/roadmap') ? 'var(--cedar-primary)' : 'var(--text-secondary)' }}
                />
                {!effectiveCollapsed && <span className="text-sm font-medium">Roadmap</span>}
              </Link>
              {/* Divider — after Roadmap, before Favorites */}
              <div style={{ height: '1px', backgroundColor: 'var(--cedar-limestone)', margin: '0 8px 8px 8px' }} />

              {/* Favorites section — Phase 6 polish v23. Renders only when
                  the user has pinned at least one item. Drag-to-reorder via
                  @dnd-kit/sortable (same family the Roadmap Kanban uses).
                  Hidden when empty so it doesn't add a wasted divider band.
                  Per the user spec: render in pin order + drag-reorder. */}
              {validFavorites.length > 0 && (
                <>
                  <FavoritesSection
                    paths={validFavorites}
                    itemsByPath={navItemsByPath}
                    isActive={isActive}
                    sidebarCollapsed={effectiveCollapsed}
                    onReorder={persistFavorites}
                    onUnpin={toggleFavorite}
                  />
                  {/* Divider — after Favorites, before the rest of the nav */}
                  <div style={{ height: '1px', backgroundColor: 'var(--cedar-limestone)', margin: '0 8px 8px 8px' }} />
                </>
              )}

              <ul className="space-y-1">
                {navigationGroups.map((group) => {
                  const GroupIcon = group.icon;
                  const isExpanded = expandedGroups.has(group.label);
                  const groupActive = isGroupActive(group);

                  return (
                    <li key={group.label}>
                      {/* Group Header — when sidebar is expanded this is
                          a clickable dropdown. When collapsed it becomes a
                          Link to the first item in the group so a single
                          click goes somewhere useful (the icon rail is
                          navigation-first, not nested-tree-first). */}
                      {effectiveCollapsed ? (
                        <Link
                          to={group.items[0]?.path ?? '/admin'}
                          title={group.label}
                          aria-label={group.label}
                          className={`w-full flex items-center justify-center py-2.5 rounded-lg transition-colors ${
                            groupActive ? '' : 'hover:bg-gray-50'
                          }`}
                          style={groupActive ? { backgroundColor: 'rgba(31, 108, 79, 0.08)' } : {}}
                        >
                          <GroupIcon className="h-4 w-4 flex-shrink-0" style={{ color: groupActive ? 'var(--cedar-primary)' : 'var(--text-tertiary)' }} />
                        </Link>
                      ) : (
                        <button
                          onClick={() => toggleGroup(group.label)}
                          className={`w-full flex items-center px-3 py-2.5 rounded-lg transition-colors text-left ${
                            groupActive ? '' : 'hover:bg-gray-50'
                          }`}
                          style={groupActive ? { backgroundColor: 'rgba(31, 108, 79, 0.08)' } : {}}
                        >
                          <GroupIcon className="h-4 w-4 mr-2.5 flex-shrink-0" style={{ color: groupActive ? 'var(--cedar-primary)' : 'var(--text-tertiary)' }} />
                          <span
                            // Phase 6 polish v14 (2026-05-11): group
                            // labels were text-xs (12px) while their
                            // child items were text-sm (14px) — the
                            // hierarchy read backwards. Bump group to
                            // text-sm and keep uppercase + bold for
                            // visual differentiation from children.
                            className="flex-1 text-sm font-semibold uppercase tracking-wide"
                            style={{ color: groupActive ? 'var(--cedar-primary)' : 'var(--text-secondary)', fontFamily: "'League Spartan', sans-serif" }}
                          >
                            {group.label}
                          </span>
                          {isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5" style={{ color: 'var(--text-tertiary)' }} />
                            : <ChevronRight className="h-3.5 w-3.5" style={{ color: 'var(--text-tertiary)' }} />
                          }
                        </button>
                      )}

                      {/* Group Items — collapsible. Hidden entirely when
                          the sidebar is in rail mode (the group icon is
                          its own navigation entry). */}
                      {!effectiveCollapsed && isExpanded && (
                        <ul className="mt-0.5 ml-3 space-y-0.5">
                          {group.items.map((item) => {
                            const Icon = item.icon;
                            const favored = isFavorited(item.path);
                            return (
                              <li key={item.path} className="group/navitem relative">
                                <Link
                                  to={item.path}
                                  className={`flex items-center px-3 py-2 rounded-md transition-colors text-sm ${
                                    isActive(item.path) ? 'font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                  }`}
                                  style={isActive(item.path) ? { backgroundColor: 'rgba(31, 108, 79, 0.06)', color: 'var(--cedar-primary)' } : {}}
                                >
                                  <Icon className="h-4 w-4 mr-2.5 flex-shrink-0" style={isActive(item.path) ? { color: 'var(--cedar-primary)' } : { color: 'var(--text-tertiary)' }} />
                                  <span className="flex-1">{item.label}</span>
                                  {item.badge && (
                                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full min-w-[20px] mr-1">
                                      {item.badge}
                                    </span>
                                  )}
                                </Link>
                                {/* Phase 6 polish v23 (2026-05-13): Star toggle
                                    overlays each nav item's right edge. Hidden
                                    by default; revealed on hover OR when the
                                    item is already favorited (so the user can
                                    always tell at a glance + unpin). */}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleFavorite(item.path);
                                  }}
                                  aria-label={favored ? `Unpin ${item.label}` : `Pin ${item.label} to favorites`}
                                  title={favored ? 'Unpin from favorites' : 'Pin to favorites'}
                                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity ${
                                    favored
                                      ? 'opacity-100'
                                      : 'opacity-0 group-hover/navitem:opacity-100 focus:opacity-100'
                                  } hover:bg-gray-100`}
                                >
                                  <Star
                                    className="h-3.5 w-3.5"
                                    style={
                                      favored
                                        ? { color: 'var(--status-warning)', fill: 'var(--status-warning)' }
                                        : { color: 'var(--text-tertiary)' }
                                    }
                                  />
                                </button>
                              </li>
                            );
                          })}

                          {/* CC LLMs is now its own top-level nav group (peer to
                              Platform Analytics) — see the CC LLMs entry in
                              navigationGroups above. The previous "Content &
                              Coaching" parent and this nested sub-group were
                              removed in the analytics-overview-audit PR. */}

                          {/* CC Marketing sub-group — nested under Marketing */}
                          {group.label === 'Marketing & Comms' && (
                            <li>
                              <button
                                onClick={() => setMarketingExpanded(!marketingExpanded)}
                                className={`w-full flex items-center px-3 py-2 rounded-md transition-colors text-sm ${
                                  isMarketingActive ? 'font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                                style={isMarketingActive ? { backgroundColor: 'rgba(31, 108, 79, 0.06)', color: 'var(--cedar-primary)' } : {}}
                              >
                                <TrendingUp className="h-4 w-4 mr-2.5 flex-shrink-0" style={isMarketingActive ? { color: 'var(--cedar-primary)' } : { color: 'var(--text-tertiary)' }} />
                                <span className="flex-1 text-left">CC Marketing</span>
                                {marketingExpanded
                                  ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                                  : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                                }
                              </button>
                              {marketingExpanded && (
                                <ul className="ml-6 mt-0.5 space-y-0.5">
                                  <li>
                                    <Link
                                      to="/admin/marketing/blog"
                                      className={`flex items-center px-2 py-1.5 rounded-md text-sm transition-colors ${isActive('/admin/marketing/blog') ? 'font-medium' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`}
                                      style={isActive('/admin/marketing/blog') ? { backgroundColor: 'rgba(31, 108, 79, 0.06)', color: 'var(--cedar-primary)' } : {}}
                                    >
                                      <PenTool className="h-3.5 w-3.5 mr-2" style={isActive('/admin/marketing/blog') ? { color: 'var(--cedar-primary)' } : { color: 'var(--border-strong)' }} />
                                      Blog CMS
                                    </Link>
                                  </li>
                                </ul>
                              )}
                            </li>
                          )}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Dynamic Quick Stats — only when expanded. The 56px rail
                doesn't have room for the labels + values, and collapsing
                the sidebar is specifically for reclaiming horizontal
                space for the main content. */}
            {!effectiveCollapsed && (
            <div className="mt-4 bg-white p-4" style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--card-drop)' }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--cedar-dark)', fontFamily: "'League Spartan', sans-serif" }}>Quick Stats</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Active Users</span>
                  <span className="font-medium">
                    {quickStats ? quickStats.active_users : '...'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Requests</span>
                  <span className="font-medium">
                    {quickStats ? quickStats.total_sessions : '...'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">System Health</span>
                  <span className="font-medium" style={{ color: quickStats && quickStats.system_health > 70 ? 'var(--cedar-primary)' : undefined }}>
                    {quickStats ? `${quickStats.system_health}%` : '...'}
                  </span>
                </div>
              </div>
            </div>
            )}
          </nav>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};


// ────────────────────────────────────────────────────────────────────
// Phase 6 polish v23 (2026-05-13) — Favorites section
//
// Renders the user's pinned nav items in order, with drag-to-reorder
// powered by @dnd-kit/sortable. Empty state is the caller's
// responsibility — when validFavorites.length === 0 the parent skips
// rendering this section entirely (so we never show a wasted divider).
//
// The drag implementation deliberately uses PointerSensor (with an
// 8px activation distance) so clicks on a favorited row still
// navigate via <Link>. Without the distance threshold, every click
// would be interpreted as a drag and routing would never fire.
// ────────────────────────────────────────────────────────────────────

interface FavoritesSectionProps {
  paths: string[];                       // ordered pin paths
  itemsByPath: Record<string, NavItem>;  // flat map for lookups
  isActive: (path: string) => boolean;
  sidebarCollapsed: boolean;
  onReorder: (next: string[]) => void;
  onUnpin: (path: string) => void;
}

const FavoritesSection: React.FC<FavoritesSectionProps> = ({
  paths,
  itemsByPath,
  isActive,
  sidebarCollapsed,
  onReorder,
  onUnpin,
}) => {
  // 8px activation distance means clicks on the row still navigate;
  // only an actual drag motion engages the sortable.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = paths.indexOf(active.id as string);
    const newIndex = paths.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(paths, oldIndex, newIndex));
  };

  return (
    <div className="mb-2">
      {!sidebarCollapsed && (
        <div
          className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider"
          style={{ color: 'var(--text-tertiary)', fontFamily: "'League Spartan', sans-serif" }}
        >
          Favorites
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={paths} strategy={verticalListSortingStrategy}>
          <ul className="space-y-0.5">
            {paths.map((path) => {
              const item = itemsByPath[path];
              if (!item) return null;  // defensive — parent already filters
              return (
                <SortableFavoriteRow
                  key={path}
                  path={path}
                  item={item}
                  active={isActive(path)}
                  sidebarCollapsed={sidebarCollapsed}
                  onUnpin={onUnpin}
                />
              );
            })}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};

interface SortableFavoriteRowProps {
  path: string;
  item: NavItem;
  active: boolean;
  sidebarCollapsed: boolean;
  onUnpin: (path: string) => void;
}

const SortableFavoriteRow: React.FC<SortableFavoriteRowProps> = ({
  path,
  item,
  active,
  sidebarCollapsed,
  onUnpin,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: path });
  const Icon = item.icon;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    // Lift the row visually while dragging so the user sees what's moving
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="group/fav relative"
      {...attributes}
    >
      <Link
        to={item.path}
        title={sidebarCollapsed ? item.label : undefined}
        className={`flex items-center rounded-md transition-colors text-sm ${
          sidebarCollapsed ? 'justify-center py-2' : 'px-3 py-2'
        } ${active ? 'font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
        style={active ? { backgroundColor: 'rgba(31, 108, 79, 0.06)', color: 'var(--cedar-primary)' } : {}}
      >
        {/* Drag handle — only visible when sidebar is expanded. The
            handle is what receives the listeners so the rest of the
            row click navigates normally. */}
        {!sidebarCollapsed && (
          <button
            type="button"
            {...listeners}
            aria-label={`Drag to reorder ${item.label}`}
            title="Drag to reorder"
            className="mr-1 -ml-1 cursor-grab active:cursor-grabbing opacity-0 group-hover/fav:opacity-100 transition-opacity"
            onClick={(e) => {
              // Stop the click from bubbling to the Link
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <GripVertical className="h-3.5 w-3.5" style={{ color: 'var(--text-tertiary)' }} />
          </button>
        )}
        <Icon
          className={`h-4 w-4 flex-shrink-0 ${sidebarCollapsed ? '' : 'mr-2.5'}`}
          style={{ color: active ? 'var(--cedar-primary)' : 'var(--text-tertiary)' }}
        />
        {!sidebarCollapsed && (
          <>
            <span className="flex-1 truncate">{item.label}</span>
            {item.badge && (
              <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full min-w-[20px] mr-1">
                {item.badge}
              </span>
            )}
          </>
        )}
      </Link>
      {!sidebarCollapsed && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onUnpin(path);
          }}
          aria-label={`Unpin ${item.label}`}
          title="Unpin"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover/fav:opacity-100 focus:opacity-100 transition-opacity hover:bg-gray-100"
        >
          <Star className="h-3.5 w-3.5" style={{ color: 'var(--status-warning)', fill: 'var(--status-warning)' }} />
        </button>
      )}
    </li>
  );
};
